// Polls /version.json (generated at build time, served no-cache) every few
// minutes. When the build hash changes, surfaces a toast prompting the user
// to reload — so they never have to manually hard-refresh to pick up a new
// deploy.
//
// /version.json is written by scripts/write-version.mjs during `npm run build`
// and looks like: { "buildId": "2026-06-16T12:34:56.789Z-abc123" }

import { toast } from "sonner";

const VERSION_URL = "/version.json";
const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

let currentBuildId: string | null = null;
let started = false;
let promptShown = false;

async function fetchBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { buildId?: string };
    return typeof data.buildId === "string" ? data.buildId : null;
  } catch {
    return null;
  }
}

function showUpdateToast() {
  if (promptShown) return;
  promptShown = true;
  toast("A new version is available", {
    description: "Reload to get the latest updates.",
    duration: Infinity,
    action: {
      label: "Reload",
      onClick: () => window.location.reload(),
    },
    onDismiss: () => {
      promptShown = false;
    },
  });
}

async function checkOnce() {
  const latest = await fetchBuildId();
  if (!latest) return;
  if (currentBuildId == null) {
    currentBuildId = latest;
    return;
  }
  if (latest !== currentBuildId) {
    showUpdateToast();
  }
}

export function startVersionCheck() {
  if (started) return;
  started = true;
  // Initial baseline + interval
  void checkOnce();
  setInterval(() => {
    void checkOnce();
  }, POLL_INTERVAL_MS);
  // Also re-check whenever the tab regains focus — covers users who leave
  // the tab open for hours.
  window.addEventListener("focus", () => {
    void checkOnce();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkOnce();
  });
}
